import { ErrorComponent } from '@/components/generalError';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TreatmentPlan } from '@/components/cockpit/treatmentPlan';
import { PatientDetails } from '@/components/patientDetails/overview/patientDetails';
import { PersonIcon } from '@/assets';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { Checklist } from '@/components/cockpit/checklist';
import { assembleRoute } from '@/types/routes';
import { CueingProfileSubstancePreview } from '@/components/cockpit/cueReactivity/cueingProfileSubstancePreview';
import {
  isProcedureCompleted,
  isProcedureCancelled,
} from '@/lib/procedureStatus';
import { useAcceptCockpitChecklist } from '@/api/mutations/procedures';

export const Route = createFileRoute('/_plain/cockpit/$procedureId/overview')({
  errorComponent: ErrorComponent,
  component: RouteComponent,
});

function RouteComponent() {
  const { procedureId } = Route.useParams();
  const navigate = useNavigate();
  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );
  const { data: patient } = useSuspenseQuery(
    getPatientQueryOptions(procedure.patient.id),
  );
  const acceptCockpitChecklist = useAcceptCockpitChecklist();

  const handleChecklistConfirm = () => {
    acceptCockpitChecklist.mutate(
      { procedureId },
      {
        // We shouldn't stop the user from entering treatment if this request fails
        onSettled: () => {
          void navigate({
            to: assembleRoute('cockpitTreatment', { procedureId }),
          });
        },
      },
    );
  };

  // If the checklist is already accepted, redirect to treatment view
  if (procedure.isCockpitChecklistAccepted) {
    void navigate({
      to: assembleRoute('cockpitTreatment', { procedureId }),
      replace: true,
    });
    return null;
  }

  // If procedure already completed, redirect user back to standard procedure view
  if (isProcedureCompleted(procedure) || isProcedureCancelled(procedure)) {
    void navigate({
      to: assembleRoute('procedureDetails', { procedureId }),
      replace: true,
    });
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-space-lg">
      <div className="flex flex-col gap-space-lg">
        <TreatmentPlan
          activeTargets={ procedure.activeTargets }
          procedureId={ procedureId }
          power={ procedure.config?.sonicationPower }
        />
        <PatientDetails
          icon={ PersonIcon }
          readonly
          patientDetails={ patient }
          className="h-auto min-h-[100px]"
          hideEmptyNotes={ true }
        />
        <CueingProfileSubstancePreview procedureId={ procedureId } />
      </div>
      <div className="flex flex-col gap-space-lg">
        <Checklist
          onSuccess={ handleChecklistConfirm }
          procedureId={ procedureId }
        />
      </div>
    </div>
  );
}
